import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  NodeTypes,
  EdgeTypes,
  ReactFlowProvider,
  useNodesInitialized,
  useNodes,
  Node,
  Edge,
} from 'reactflow';

import { CustomEdge } from './CustomEdge';
import {
  InputFunctionNode,
  CenterFunctionNode,
  OutputFunctionNode,
} from './CustomNodes';
import {
  calculateHeight,
  getInspectorNodePosition,
  layoutNodes,
} from './utils';
import { NodeData, EdgeData } from './types';
import * as Styled from './FlowChart.styles';
import 'reactflow/dist/style.css';
import _ from 'lodash';
import { useDebouncedState } from '../../hooks';

const proOptions = { hideAttribution: true };

const edgeTypes: EdgeTypes = {
  default: CustomEdge,
};

const nodeTypes: NodeTypes = {
  inputFunction: InputFunctionNode,
  outputFunction: OutputFunctionNode,
  centerFunction: CenterFunctionNode,
};

export interface FlowChartProps {
  height: number;
  width: number;
  initialNodes: Node<NodeData>[];
  initialEdges: Edge<EdgeData>[];
  loading?: boolean;

  onNodeClick?: (node: Node<NodeData>) => void;
  onCTANodeClick?: (node: Node<NodeData>) => void;

  EdgeInspectorComponent?: React.ComponentType<{ edge: Edge<EdgeData> }>;
  NodeInspectorComponent?: React.ComponentType<{ node: Node<NodeData> }>;
}

export type ActiveHover = {
  type: 'node' | 'edge';
  hoveredNode: Node<NodeData> | null;
  hoveredEdge: Edge<EdgeData> | null;
};

export function InternalFlowChart({
  width = 1100,
  initialNodes,
  initialEdges,
  loading,
  onNodeClick,
  onCTANodeClick,
  EdgeInspectorComponent,
  NodeInspectorComponent,
}: FlowChartProps) {
  const flowRef = React.useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] =
    useNodesState<NodeData>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const initializedNodes = useNodes();
  const nodesInitialized = useNodesInitialized();

  const [activeHover, setActiveHover] = useDebouncedState<ActiveHover | null>(
    null,
    200,
  );

  const effectiveHeight = useMemo(() => {
    return nodesInitialized ? calculateHeight(initializedNodes) : 0;
  }, [initializedNodes, initialNodes]);

  useEffect(() => {
    if (nodesInitialized) {
      const layoutHeight = calculateHeight(initializedNodes);
      const nodesToLayout = _.isEqual(
        initializedNodes.map((n) => n.id),
        initialNodes.map((n) => n.id),
      )
        ? initializedNodes
        : initialNodes;
      const layoutedNodes = layoutNodes(
        nodesToLayout as Node<NodeData>[],
        layoutHeight,
        width,
      );
      setNodes(layoutedNodes);
      setEdges(initialEdges);
    }
  }, [
    nodesInitialized,
    width,
    initialNodes,
    initialEdges,
    effectiveHeight,
    loading,
  ]);

  const inspectorNodePosition = useMemo(() => {
    if (activeHover) {
      return getInspectorNodePosition(
        nodes,
        width,
        flowRef.current,
        activeHover,
      );
    }
    return;
  }, [activeHover, nodes, width]);

  return (
    <Styled.Container
      style={{
        height: `${effectiveHeight}px`,
        width: `${width}px`,
      }}
    >
      <ReactFlowStyled
        ref={flowRef}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        edgeTypes={edgeTypes}
        nodeTypes={nodeTypes}
        proOptions={proOptions}
        defaultViewport={{
          zoom: 0.99,
          x: 5,
          y: 1,
        }}
        preventScrolling={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        onNodeClick={(e, node) => {
          if (node.data.notClickable) return;
          if (node.data.cta && onCTANodeClick) {
            setActiveHover(null);
            onCTANodeClick(node);
          } else {
            if (onNodeClick) {
              setActiveHover(null);
              onNodeClick(node);
            }
          }
        }}
        onNodeMouseEnter={(e, node) => {
          if (node.type === 'centerFunction') {
            setActiveHover(
              {
                type: 'node',
                hoveredNode: node,
                hoveredEdge: null,
              },
              true,
            );

            const [newEdges, newNodes] = highlightCenterNode(edges, nodes);
            setEdges(newEdges);
            setNodes(newNodes);
            return;
          }

          // Find the connected edge
          const connectedEdge = edges.find(
            (edge) => edge.source === node.id || edge.target === node.id,
          );

          if (!connectedEdge) return;

          const [newEdges, newNodes] = highlightEdgeAndNodes(
            edges,
            nodes,
            connectedEdge,
          );
          setEdges(newEdges);
          setNodes(newNodes);

          // Show inspector only when some data is available
          if (connectedEdge.data?.showInspector) {
            setActiveHover(
              {
                type: 'edge',
                hoveredEdge: connectedEdge,
                hoveredNode: null,
              },
              true,
            );
          }
        }}
        onNodeMouseLeave={() => {
          const [newEdges, newNodes] = dehighlightEdgeAndNodes(edges, nodes);
          setActiveHover(null);
          setEdges(newEdges);
          setNodes(newNodes);
        }}
        onEdgeMouseEnter={(e, edge) => {
          const [newEdges, newNodes] = highlightEdgeAndNodes(
            edges,
            nodes,
            edge,
          );

          setEdges(newEdges);
          setNodes(newNodes);

          // Show inspector only when some data is available
          if (edge.data.showInspector) {
            setActiveHover(
              {
                type: 'edge',
                hoveredEdge: edge,
                hoveredNode: null,
              },
              true,
            );
          }
        }}
        onEdgeMouseLeave={() => {
          const [newEdges, newNodes] = dehighlightEdgeAndNodes(edges, nodes);
          setActiveHover(null);
          setEdges(newEdges);
          setNodes(newNodes);
        }}
      >
        {activeHover && (
          <Styled.InspectorContainer style={inspectorNodePosition}>
            {EdgeInspectorComponent && activeHover.hoveredEdge && (
              <EdgeInspectorComponent edge={activeHover.hoveredEdge} />
            )}
            {NodeInspectorComponent && activeHover.hoveredNode && (
              <NodeInspectorComponent node={activeHover.hoveredNode} />
            )}
          </Styled.InspectorContainer>
        )}
      </ReactFlowStyled>
    </Styled.Container>
  );
}

export function FlowChart(props: FlowChartProps) {
  return (
    <ReactFlowProvider>
      <InternalFlowChart {...props} />
    </ReactFlowProvider>
  );
}

const ReactFlowStyled = styled(ReactFlow)`
  /* TODO: This is a hack that might not be a good idea */
  overflow: visible !important;
`;

function highlightEdgeAndNodes(
  edges: Edge<EdgeData>[],
  nodes: Node<NodeData>[],
  edge: Edge<EdgeData>,
): [Edge<EdgeData>[], Node<NodeData>[]] {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  const newEdges: Edge<EdgeData>[] = edges.map((e) => ({
    ...e,
    data: { ...e.data, highlight: e.id === edge.id },
  }));

  const newNodes: Node<NodeData>[] = nodes.map((n) => ({
    ...n,
    data: {
      ...n.data,
      highlight: getNodeHighlight(n, sourceNode, targetNode),
    },
  }));

  return [newEdges, newNodes];
}

function highlightCenterNode(
  edges: Edge<EdgeData>[],
  nodes: Node<NodeData>[],
): [Edge<EdgeData>[], Node<NodeData>[]] {
  const newEdges: Edge<EdgeData>[] = edges.map((e) => ({
    ...e,
    data: { ...e.data, highlight: false },
  }));

  const newNodes: Node<NodeData>[] = nodes.map((n) => ({
    ...n,
    data: {
      ...n.data,
      highlight: n.type === 'centerFunction',
    },
  }));

  return [newEdges, newNodes];
}

function getNodeHighlight(
  node: Node<NodeData>,
  sourceNode?: Node<NodeData>,
  targetNode?: Node<NodeData>,
) {
  if (node.id === sourceNode?.id || node.id === targetNode?.id) {
    return true; // highlight
  }

  if (sourceNode?.type === 'centerFunction' && node.type === 'outputFunction') {
    return undefined; // Don't highlihgt and don't hide
  }

  if (targetNode?.type === 'centerFunction' && node.type === 'inputFunction') {
    return undefined; // Don't highlihgt and don't hide
  }

  return false; // hide
}

function dehighlightEdgeAndNodes(
  edges: Edge<EdgeData>[],
  nodes: Node<NodeData>[],
): [Edge<EdgeData>[], Node<NodeData>[]] {
  const newEdges: Edge<EdgeData>[] = edges.map((e) => ({
    ...e,
    data: { ...e.data, highlight: undefined },
  }));

  const newNodes: Node<NodeData>[] = nodes.map((n) => ({
    ...n,
    data: { ...n.data, highlight: undefined },
  }));

  return [newEdges, newNodes];
}
